import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { IDS } from "@/lib/testIds";
import { Plus, ArrowRight } from "@phosphor-icons/react";

const STATUS_COLOR = {
  in_progress: "bg-primary text-primary-foreground",
  submitted: "bg-yellow-500 text-black",
  approved: "bg-green-600 text-white",
  rejected: "bg-destructive text-destructive-foreground",
  reopened: "bg-blue-600 text-white",
  draft: "bg-muted text-muted-foreground",
};

export default function Sessions() {
  const [sessions, setSessions] = useState([]);
  const [name, setName] = useState("");
  const [locs, setLocs] = useState([]);
  const [locId, setLocId] = useState("");
  const nav = useNavigate();

  const load = useCallback(async () => {
    const { data } = await api.get("/inventory/sessions");
    setSessions(data);
  }, []);

  useEffect(() => {
    load();
    api.get("/locations").then((r) => setLocs(r.data)).catch(() => {});
  }, [load]);

  const create = async () => {
    try {
      const { data } = await api.post("/inventory/sessions", {
        name: name.trim() || `Count ${new Date().toLocaleString()}`,
        location_id: locId || null,
      });
      nav(`/sessions/${data.session_id}`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="p-4 md:p-6 max-w-6xl">
      <div className="mb-4">
        <div className="label-tech mb-1">Operations</div>
        <h1 className="font-display font-black text-3xl md:text-4xl tracking-tight">Count Sessions</h1>
      </div>

      <div className="card-flat border-border p-4 mb-4">
        <div className="label-tech mb-2">Start New Session</div>
        <div className="grid grid-cols-1 md:grid-cols-[1fr_240px_180px] gap-2">
          <input
            data-testid={IDS.sessionName}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Session name"
            className="h-11 px-3 border border-border bg-background rounded-sm text-sm outline-none focus:ring-2 focus:ring-primary"
          />
          <select
            value={locId}
            onChange={(e) => setLocId(e.target.value)}
            className="h-11 px-2 border border-border bg-background rounded-sm text-sm"
          >
            <option value="">All locations</option>
            {locs.map((l) => (<option key={l.location_id} value={l.location_id}>{l.name}</option>))}
          </select>
          <button
            data-testid={IDS.createSession}
            onClick={create}
            className="btn-hard h-11 bg-primary text-primary-foreground font-bold uppercase tracking-widest text-xs rounded-sm hover:bg-orange-600 hover:text-white flex items-center justify-center gap-1"
          >
            <Plus size={16} weight="bold" /> Create
          </button>
        </div>
      </div>

      <div className="card-flat border-border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/60 border-b border-border">
            <tr className="text-left label-tech">
              <th className="px-3 py-2">Session</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2 hidden md:table-cell">Created</th>
              <th className="px-3 py-2 hidden sm:table-cell">By</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {sessions.map((s) => (
              <tr key={s.session_id} className="hover:bg-muted/30">
                <td className="px-3 py-2.5">
                  <div className="font-medium">{s.name}</div>
                  <div className="text-[10px] font-mono text-muted-foreground">{s.session_id}</div>
                </td>
                <td className="px-3 py-2.5">
                  <span className={`text-[10px] font-bold tracking-[0.2em] uppercase px-2 py-0.5 rounded-sm ${STATUS_COLOR[s.status] || "bg-muted"}`}>
                    {s.status.replace("_", " ")}
                  </span>
                </td>
                <td className="px-3 py-2.5 hidden md:table-cell text-xs font-mono">{new Date(s.created_at).toLocaleString()}</td>
                <td className="px-3 py-2.5 hidden sm:table-cell text-xs">{s.created_by}</td>
                <td className="px-3 py-2.5 text-right">
                  <Link
                    to={`/sessions/${s.session_id}`}
                    className="text-primary hover:underline text-xs font-bold uppercase tracking-widest inline-flex items-center gap-1"
                  >
                    Open <ArrowRight size={12} weight="bold" />
                  </Link>
                </td>
              </tr>
            ))}
            {sessions.length === 0 && (
              <tr><td colSpan={5} className="p-8 text-center text-sm text-muted-foreground">No sessions yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
